/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_NO: Industry[] = [
  {
    slug: "construction",
    name: "Bygg og anlegg",
    headline: "Tidsregistrering for byggelag — stedsverifisert innsjekk, prosjekttimer og budsjettkontroll, i én app",
    intro: "Arbeidslag spredt over flere steder, timer rablet ned på papir, underleverandørtimer umulige å avstemme. Done-it gir hvert sted en vanntett digital timeliste som mater lønnssystemet ditt med ett klikk.",
    pains: [
      {
        title: "Ingen pålitelig registrering av hvem som jobbet hvor",
        body: "Med flere aktive steder og roterende arbeidslag er manuelle timelister feil per design. Arbeidere runder opp, glemmer registreringer eller fyller dem inn dager senere — og etterlater deg med lønnstall du ikke kan forsvare.",
      },
      {
        title: "Budsjettoverskridelser dukker opp for sent",
        body: "Når du merker at et sted har brukt opp de planlagte timene, er marginen allerede borte. Det finnes ingen sanntids nedtelling — bare en ubehagelig overraskelse ved faktureringstidspunktet.",
      },
      {
        title: "Lønnsforberedelser spiser opp timer hver måned",
        body: "Å samle inn timelister, avstemme dem mot prosjekter og forberede lønnseksporten tar dager. Hver registrering er en potensiell feil.",
      },
    ],
    features: [
      {
        title: "Stedsverifisert innsjekk per sted",
        body: "Ett trykk sjekker arbeideren inn, stedsverifisert, og knytter timene til riktig prosjekt. Ingen kortlesere, ingen terminaler — bare smarttelefonen arbeidslaget ditt allerede har med seg.",
      },
      {
        title: "Sanntids gjenstående timer per prosjekt",
        body: "Sett planlagte timer som et budsjett når du åpner prosjektet. Arbeidere ser en nedtelling av hva som er igjen; du ser det i sanntid i dashbordet ditt. Budsjettoverskridelser er synlige før de skjer.",
      },
      {
        title: "Håndtering og tilstedeværelse av underleverandører",
        body: "Legg til underleverandører i et prosjekt, tildel dem innsjekk og følg timene deres sammen med ditt eget arbeidslag — i samme oversikt.",
      },
      {
        title: "Lønnseksport med ett klikk",
        body: "Alle registrerte timer — per arbeider, per prosjekt — eksporteres til et rent regneark for lønnsleverandøren din. Ingen manuell forberedelse, ingen feil.",
      },
    ],
    compliance: {
      label: "Digital tidsregistrering",
      title: "Stedsverifisert innsjekk bygget for byggeplasser",
      body: "Done-it leverer stedsverifisert innsjekk, tidsregistrering per prosjekt og en ren lønnseksport for byggelag. For team som opererer på belgiske steder mater den også innsjekk på jobben automatisk.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Arbeidslaget kommer til stedet", body: "Hver arbeider trykker én gang for å sjekke inn. GPS bekrefter stedet, tidsstempler registreringen og knytter timene til prosjektet. Ingen papir, ingen terminal." },
      { step: "Du ser alt i sanntid", body: "Timer, gjenstående budsjett, underleverandørtilstedeværelse og eventuelt ekstraarbeid rapportert fra stedet — alt i dashbordet ditt, i sanntid." },
      { step: "Månedsslutt på minutter", body: "Eksporter alle timer per arbeider og per prosjekt. Send til lønnsleverandøren din. Done-it genererer rene, revisjonsklare rapporter automatisk." },
    ],
    stats: [
      { value: "1 trykk", label: "for å sjekke inn på ethvert sted — ingen terminal, ingen papir" },
      { value: "1 klikk", label: "lønnseksport klar for leverandøren din" },
      { value: "Sanntid", label: "visning av gjenstående timer per prosjekt og sted" },
    ],
    quote: {
      text: "Arbeidslaget vårt sjekker inn selv på stedet. Ved månedsslutt går lønn bare opp — vi sluttet helt å jakte på timelister.",
      who: "Eier, byggefirma · 14 ansatte",
      result: "5 timer i uken spart på administrasjon",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Renhold",
    headline: "Tidsregistrering for renholdsfirmaer — stedsverifisert innsjekk, oppmøtebevis og lønn i én app",
    intro: "Renholdere flytter seg mellom steder hele dagen. Done-it sporer hvem som er hvor i sanntid — stedsverifisert innsjekk på hvert sted, automatisk pausefradrag og en ren lønnseksport ved månedsslutt.",
    pains: [
      {
        title: "Å bevise tilstedeværelse på hvert sted",
        body: "Kunder og inspektører forventer vanntette oppmøteregistreringer. Manuelle ark kan bestrides. Hver registrering er stedsverifisert.",
      },
      {
        title: "Team spredt over mange steder",
        body: "Med renholdere som flytter seg mellom steder gjennom dagen, har du ingen sanntidsoversikt over hvem som jobber hvor — før noen ringer.",
      },
      {
        title: "Lønnsfeil fra avrundede timer",
        body: "Uten nøyaktige inn-/ut-tider rekonstrueres lønn fra hukommelsen. Timer rundes opp, kostnader vokser og tvister følger.",
      },
    ],
    features: [
      {
        title: "Inn- og utsjekk i sanntid",
        body: "Arbeidere trykker inn og ut på telefonen på hvert sted, stedsverifisert; tiden er nøyaktig, med pauselogging.",
        plan: "Time Basic",
      },
      {
        title: "Automatisk pausefradrag",
        body: "Obligatoriske pauser trekkes fra arbeidsdagen automatisk — ingen manuell regning, ingen tvister.",
        plan: "Time Basic",
      },
      {
        title: "Sanntidsoversikt over flere steder",
        body: "Se hvilke renholdere som er på hvilke steder akkurat nå. Oppdag mangler før kundene gjør det.",
      },
      {
        title: "Fraværsregistrering med digitale sykmeldinger",
        body: "Arbeidere laster opp sykmeldinger og fraværsdokumenter direkte fra appen. Opprett fraværstypene teamet ditt faktisk bruker.",
      },
      {
        title: "Lønnseksport med ett klikk",
        body: "Nøyaktige tidsstempler flyter rett inn i en lønnsklar eksport for leverandøren din. Ingen avskrift, ingen feil.",
      },
    ],
    compliance: {
      label: "Stedsverifisert oppmøte",
      title: "Sanntidsoppmøte på tvers av hvert renholdssted",
      body: "Done-it gir renholdsteam stedsverifisert innsjekk på hvert sted, automatisk pausefradrag og en sanntidsoversikt over hvem som er på hvilket sted akkurat nå.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Renholderen kommer til stedet", body: "Ett trykk sjekker dem inn. GPS logger stedet og tidsstempler registreringen. Pauseregistrering er innebygd i appen." },
      { step: "Du ser hvert sted i sanntid", body: "Hvem som er på hvilket sted akkurat nå — ingen telefoner, ingen gjetting. Oppdag dekningsmangler før kundene gjør det." },
      { step: "Lønn er ett klikk", body: "Alle oppmøtedata — nøyaktige tider, steder og pauser — eksporteres rent for lønnsleverandøren din." },
    ],
    stats: [
      { value: "1 trykk", label: "for å sjekke inn på ethvert sted — ingen terminal nødvendig" },
      { value: "Sanntid", label: "visning av hvem som er på hvilket sted akkurat nå" },
      { value: "0", label: "doble registreringer — timer flyter rett til lønn" },
    ],
    quote: {
      text: "Vi kan endelig bevise hvem som rengjorde hvor og når. Ikke flere oppmøteark, ikke flere kundetvister — GPS tar seg av det.",
      who: "Leder, renholdsfirma · 9 ansatte",
      result: "Null oppjustering av timer",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Montering",
    headline: "Tidsregistrering for monteringsteam — prosjekttimer, navigasjon og budsjettkontroll i én app",
    intro: "Send montørene dine til riktig adresse med alt de trenger, knytt hver arbeidet time til riktig jobb automatisk, og se gjenstående budsjett før stedet stenger. Done-it er den enkleste måten å holde monteringsprosjekter i tide og innenfor kostnad.",
    pains: [
      {
        title: "Jobbinfo når ikke felten i tide",
        body: "Arbeidslag kommer til et sted uten adressen, kontaktpersonen eller omfangsdetaljene. Den første telefonen er ti minutter med fakturerbar tid tapt før en eneste time er jobbet.",
      },
      {
        title: "Timer er ikke knyttet til riktig prosjekt",
        body: "Når arbeidere fyller inn timene sine manuelt ved dagens slutt — hvis de i det hele tatt fyller dem inn — lander timer på feil jobb, eller forsvinner helt. Fakturaer stemmer ikke med virkeligheten.",
      },
      {
        title: "Budsjettoverskridelser oppdaget for sent",
        body: "Uten en sanntids nedtelling av gjenstående timer per prosjekt, finner du ut at en jobb gikk over når du avslutter fakturaen — ikke mens du fortsatt kan reagere.",
      },
      {
        title: "Underleverandørtimer vanskelige å spore",
        body: "Underleverandører som jobber på samme sted bruker forskjellige systemer, fyller inn timer separat eller ikke i det hele tatt. Å avstemme ved månedsslutt er en treg, feilutsatt prosess.",
      },
    ],
    features: [
      {
        title: "Prosjektinfo delt direkte til appen",
        body: "Arbeidere åpner prosjektet på telefonen: adresse, planlagte timer, kontaktperson og opplastede filer (tegninger, instruksjoner, PDF-er). Alt de trenger er der før de kommer.",
        plan: "Project Basic",
      },
      {
        title: "Navigasjon til stedet med ett trykk",
        body: "Trykk én gang, så navigerer telefonen til prosjektadressen — innebygd i appen, ingen separat kartapp nødvendig.",
      },
      {
        title: "Nedtelling av gjenstående timer per prosjekt",
        body: "Sett planlagte timer når du åpner prosjektet. Arbeidere ser hvor mange timer som er igjen; du ser sanntidsstatusen i dashbordet ditt. Overskridelser er synlige før de skjer.",
        plan: "Project Basic",
      },
      {
        title: "Arbeiderinnsjekk per prosjekt",
        body: "Hver arbeider sjekker inn på riktig prosjekt. Du vet alltid hvem som er på hvilket sted akkurat nå.",
        plan: "Project Basic",
      },
      {
        title: "Logging av ekstraarbeid og materiale",
        body: "Når omfanget vokser på stedet, logger arbeidere ekstraarbeid og materialer i sanntid — med tekst og bilder — så ingenting faktureres fra hukommelsen.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Digital tidsregistrering",
      title: "Stedsverifisert innsjekk per prosjekt for monteringsteam",
      body: "Done-it gir monteringsteam stedsverifisert innsjekk per prosjekt, en sanntids nedtelling av gjenstående timer og automatisk lønnseksport — og dekker ethvert lokalt krav til tidsregistrering. For team som jobber på belgiske byggeplasser integrerer den også med CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Arbeideren åpner prosjektet på telefonen", body: "Adresse, kontakt, omfang og filopplastinger er rett der. De navigerer med ett trykk — ingen samtaler til kontoret for veibeskrivelse." },
      { step: "Timer logges automatisk til riktig jobb", body: "Innsjekk knytter hvert arbeidet minutt til riktig prosjekt. Gjenstående budsjett oppdateres i sanntid." },
      { step: "Ekstraarbeid fanges på stedet", body: "Omfangsendringer, ekstra materialer og forsinkelser logges med tekst og bilder i det øyeblikket de skjer — klart for fakturaen." },
    ],
    stats: [
      { value: "Sanntid", label: "visning av gjenstående timer per prosjekt" },
      { value: "0", label: "kontorsamtaler for stedsadresser" },
      { value: "1 klikk", label: "naviger til enhver prosjektadresse fra appen" },
    ],
    quote: {
      text: "Timene lander på riktig prosjekt automatisk. Jobbkalkyle er plutselig enkelt — vi kan se den reelle marginen før vi avslutter en jobb.",
      who: "Eier, monteringsfirma · 6 ansatte",
      result: "Reell kostnad per jobb, i sanntid",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Felttjeneste",
    headline: "Tidsregistrering for felttjenesteteam — kjørelengde, utgifter og timer fra veien, alt i én app",
    intro: "Teknikere som besøker flere kunder om dagen, kvitteringer i hanskerom, kjørelengde logget fra hukommelsen. Done-it fanger timer, kilometer og utgifter ute i felten — knyttet til riktig jobb i det øyeblikket de skjer.",
    pains: [
      {
        title: "Kjørelengde logget dager senere — om i det hele tatt",
        body: "Å be teknikere rekonstruere rutene sine ved ukens slutt betyr unøyaktige refusjoner, tvister med kunder og en voksende haug med ukrevde kilometer.",
      },
      {
        title: "Utgifter på papirkvitteringer som forsvinner",
        body: "En kvittering i et hanskerom er en kostnad som kanskje aldri kommer inn i en faktura. Den reelle kostnaden per jobb er alltid for lav.",
      },
      {
        title: "Ingen sanntidsoversikt over hvor teamet er",
        body: "Uten sanntidsinnsjekker finner du bare ut hvor en tekniker er når noe går galt og noen ringer.",
      },
      {
        title: "Vanskelig å se den faktiske marginen per jobb",
        body: "Når arbeid, reise og utgifter spores i separate systemer — eller ikke spores i det hele tatt — finnes det aldri ett enkelt kostnadstall per jobb.",
      },
    ],
    features: [
      {
        title: "Automatisk fangst av kjørelengde (Trax-it)",
        body: "GPS logger avgang, ankomst og nøyaktig distanse per tur. Konfigurer transporttyper (egen bil, firmabil, samkjøring) med egne refusjonssatser. Ingen manuell kjørebok.",
        plan: "Add-on",
      },
      {
        title: "Utgiftshåndtering på stedet",
        body: "Arbeidere knipser en kvittering ute i felten; den knyttes til jobben og er klar for refusjon — ikke jaktet på ved månedsslutt.",
        plan: "Time Basic",
      },
      {
        title: "Prosjektknyttet kostnadskontroll",
        body: "Timer, kjørelengde og utgifter samles mot jobben i sanntid. Kostnaden per jobb er synlig mens jobben fortsatt er åpen.",
        plan: "Project Basic",
      },
      {
        title: "Stedsverifisert innsjekk og sanntidsoversikt over teamet",
        body: "Hver tekniker sjekker inn på kundens sted. Du ser hvem som er hvor, akkurat nå — ingen samtaler, ingen gjetting.",
      },
      {
        title: "Lønnseksport med ett klikk",
        body: "Timer og kilometerrefusjoner eksporteres rent for lønnsleverandøren din — med de konfigurerte kjøresatsene dine brukt automatisk.",
      },
    ],
    compliance: {
      label: "Digital tids- og kjørelengderegistrering",
      title: "GPS-timer og automatisk kjørelengde for felttjenesteteam",
      body: "Done-it fanger kjørelengde automatisk per tur, bruker de konfigurerte refusjonssatsene dine og logger hver kilometer til riktig kunde eller prosjekt. EUs Tyco-dom betyr at reise fra hjemmet til første kunde teller som arbeidstid — Done-it sporer det også.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Teknikeren drar til det første oppdraget", body: "Innsjekk starter turloggen. Kilometer fanges automatisk fra avgang til ankomst — ingen manuell registrering." },
      { step: "På jobbstedet", body: "Timer knyttes til riktig prosjekt. Ekstraarbeid, materialer og kvitteringer logges i appen i det øyeblikket de skjer." },
      { step: "Månedsslutt uten kaoset", body: "Hver time, kilometer og utgift er allerede knyttet til riktig jobb. Eksporter til lønn og fakturer med reelle kostnadstall." },
    ],
    stats: [
      { value: "Automatisk", label: "fangst av kjørelengde — ingen manuell kjørebok" },
      { value: "Sanntid", label: "kostnad per jobb: timer + reise + utgifter" },
      { value: "0", label: "kilometer tapt — hver tur fanget automatisk" },
    ],
    quote: {
      text: "Kjørelengde pleide å være en månedlig hodepine. Nå logger den seg selv — hver kilometer er knyttet til riktig kunde før vi i det hele tatt er tilbake på kontoret.",
      who: "Driftsleder, felttjenestefirma · 11 ansatte",
      result: "Nøyaktig kostnad per jobb, hver gang",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "VVS",
    headline: "Tidsregistrering for VVS-teknikere — jobbkalkyle, deler og reise i én app",
    intro: "Hvert servicebesøk har tre kostnader: arbeid, deler og reise. Done-it fanger alle tre ute i felten og samler dem mot riktig jobb — så du kjenner marginen din før du avslutter fakturaen.",
    pains: [
      {
        title: "Servicebesøk er vanskelige å kostnadsberegne nøyaktig",
        body: "Arbeid, deler og reise ligger i forskjellige systemer — eller forskjellige lommer. Den reelle kostnaden av en utrykning blir først tydelig lenge etter at teknikeren har forlatt stedet.",
      },
      {
        title: "Ingen bevis på servicetid",
        body: "Når en kunde bestrider hvor lang tid en reparasjon tok, er en papir-timeliste lett å bestride. GPS-bekreftede inn- og utsjekkstidsstempler er det ikke.",
      },
      {
        title: "Vedlikeholdskontrakter eroderer marginen stille",
        body: "På gjentakende vedlikeholdskontrakter akkumuleres små overskridelser over mange besøk. Uten en sanntids timeteller finner du først ut at kontrakten er under vann ved fornyelse.",
      },
      {
        title: "Teknikere glemmer å logge pauser",
        body: "Manuelle tidsregistreringer går glipp av pauser, noe som fører til overbetaling og lønn som ikke stemmer med virkeligheten.",
      },
    ],
    features: [
      {
        title: "Prosjektbasert tidsregistrering",
        body: "Hvert servicebesøk eller vedlikeholdskontrakt er et prosjekt. Timer, reise og materialer samles i sanntid — og gir deg sanntidsoversikt over marginen.",
        plan: "Project Basic",
      },
      {
        title: "Ekstraarbeid og materialer, logget på stedet",
        body: "Når en jobb vokser, logger teknikere ekstraarbeid og deler med tekst og bilder før de drar. Ingenting faktureres fra hukommelsen.",
        plan: "Project Basic",
      },
      {
        title: "Kjørelengde- og transportkostnader",
        body: "Kilometer per utrykning logges automatisk og kostnadsføres mot jobben. Konfigurer refusjonssatser per kjøretøytype.",
        plan: "Add-on",
      },
      {
        title: "Automatisk pausefradrag",
        body: "Obligatoriske pauser trekkes automatisk fra total tid — ingen tvister, ingen lønnsfeil.",
      },
      {
        title: "Stedsverifisert innsjekk og tilstedeværelsesbevis",
        body: "Tidsstemplet, GPS-bekreftet innsjekk på hvert sted. Hver registrering er stedsverifisert.",
      },
    ],
    compliance: {
      label: "Digital tids- og kostnadsregistrering",
      title: "Full jobbkalkyle for VVS-servicebesøk",
      body: "Done-it gir VVS-team stedsverifisert innsjekk per servicebesøk, sanntidsoversikt over kostnad per jobb (arbeid + reise + deler) og en ren lønnseksport — og oppfyller ethvert lokalt krav til tidsregistrering uten ekstra maskinvare.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Teknikeren sjekker inn på stedet", body: "GPS tidsstempler ankomsten og knytter timer til servicebesøket eller vedlikeholdskontrakten. Ingen papir, ingen terminal." },
      { step: "Ekstraarbeid fanges på stedet", body: "Deler brukt, ekstra timer og omfangsendringer logges i appen med bilder — før man forlater stedet." },
      { step: "Jobbkostnaden er synlig umiddelbart", body: "Arbeid, reise og materialer summeres mot prosjektet i sanntid. Du ser marginen før du utsteder fakturaen." },
    ],
    stats: [
      { value: "3", label: "kostnadstyper fanget per utrykning: arbeid, reise, deler" },
      { value: "Sanntid", label: "oversikt over margin per servicekontrakt" },
      { value: "0", label: "overraskelser ved faktureringstidspunktet — kostnader synlige i sanntid" },
    ],
    quote: {
      text: "Vi vet endelig hva hver servicekontrakt faktisk koster oss. Marginoverraskelsene stoppet den dagen vi begynte å bruke Done-it.",
      who: "Eier, VVS-firma · 7 teknikere",
      result: "Reell kostnad per servicebesøk, i sanntid",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elektrikere",
    headline: "Tidsregistrering for elektrikere — timer per prosjekt, budsjettkontroll og navigasjon i én app",
    intro: "Timer per prosjekt, en sanntids nedtelling av gjenstående budsjett og navigasjon til stedet med ett trykk — bygget for små elektroteam som trenger prosjektkontroll uten den administrative belastningen.",
    pains: [
      {
        title: "Timer er ikke knyttet til riktig prosjekt",
        body: "Når elektrikere jobber på tvers av flere jobber i en uke, fører manuell fordeling av timer ved dagens slutt til feilattribuerte kostnader, faktureringstvister og lønn du ikke kan forklare.",
      },
      {
        title: "Budsjettoverskridelser oppdaget for sent",
        body: "Uten en sanntidsoversikt over timer brukt mot timer budsjettert, finner du ut at et prosjekt gikk over når du sender fakturaen — ikke når du fortsatt kunne reagere.",
      },
      {
        title: "Tid bortkastet på å finne stedet",
        body: "Adresser tastet inn i en separat app, samtaler til kontoret for veibeskrivelse, sene ankomster på kundesteder. Hvert bortkastet minutt er et fakturerbart minutt tapt.",
      },
      {
        title: "Underleverandørtimer vanskelige å avstemme",
        body: "På større installasjoner sporer underleverandørelektrikere sine egne timer separat. Å få en samlet oversikt over arbeidskostnadene ved slutten av et prosjekt er en manuell, feilutsatt jobb.",
      },
    ],
    features: [
      {
        title: "Prosjekttimer med sanntids gjenstående budsjett",
        body: "Hvert arbeidet minutt knyttes til riktig prosjekt automatisk. En sanntids nedtelling viser hvor mange timer som gjenstår mot budsjettet — for deg og for arbeideren på stedet.",
        plan: "Project Basic",
      },
      {
        title: "Navigasjon til stedet med ett trykk",
        body: "Prosjektadressen er i appen. Ett trykk åpner navigasjon — ingen søking, ingen separat kartapp, ingen samtaler til kontoret.",
      },
      {
        title: "Arbeiderinnsjekk per prosjekt",
        body: "Hver elektriker sjekker inn på riktig jobb. Du vet alltid hvem som er på hvilket sted akkurat nå.",
        plan: "Project Basic",
      },
      {
        title: "Ekstraarbeid logget på stedet",
        body: "Når omfanget endres — en uventet kurs, ekstra materialer — logges det i appen med bilder, før arbeidslaget drar.",
        plan: "Project Basic",
      },
      {
        title: "Overtidsregistrering med fleksibel kompensasjon",
        body: "Overtid logges i det øyeblikket den skjer. Arbeidere velger å få den utbetalt eller konvertert til avspasering.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Stedsverifisert innsjekk per prosjekt",
      title: "Sanntids budsjettkontroll og stedsverifisert innsjekk for elektroteam",
      body: "Done-it gir elektrikere stedsverifisert innsjekk per prosjekt, et sanntids gjenstående-timer-budsjett og lønnseksport — og oppfyller ethvert lokalt krav til tidsregistrering. For team på belgiske byggeplasser integrerer den også med CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Elektrikeren navigerer til jobben", body: "Adresse og omfang er allerede i appen. Ett trykk åpner navigasjon — ingen samtaler, ingen forvirring." },
      { step: "Timer knyttes til riktig prosjekt automatisk", body: "Innsjekk på stedet knytter hvert arbeidet minutt til riktig prosjekt. Gjenstående-timer-telleren oppdateres i sanntid." },
      { step: "Omfangsendringer blir registrert", body: "Ekstraarbeid og materialer logges på stedet, før man drar. Fakturaen gjenspeiler hva som faktisk skjedde." },
    ],
    stats: [
      { value: "Sanntid", label: "visning av gjenstående timer per prosjekt" },
      { value: "0", label: "kontorsamtaler for stedsadresser" },
      { value: "1 trykk", label: "for å sjekke inn på ethvert sted — ingen terminal nødvendig" },
    ],
    quote: {
      text: "Vi vet i sanntid om et prosjekt går over budsjett. Det alene har reddet oss fra to dårlige overraskelser i år.",
      who: "Eier, elektrofirma · 5 elektrikere",
      result: "Budsjettoverskridelser fanget før de skjer",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Rørlegging",
    headline: "Tidsregistrering for rørleggerteam — rask innsjekk, nøyaktig lønn og tilstedeværelsesbevis for hver utrykning",
    intro: "Akuttutrykninger, flere jobber per dag, timer fylt inn fra hukommelsen ved ukens slutt — eller ikke i det hele tatt. Done-it gjør det til en jobb med ett trykk på hvert sted og en lønn med ett klikk ved månedsslutt.",
    pains: [
      {
        title: "Utrykningstimer rekonstruert fra hukommelsen",
        body: "Med flere jobber på en dag har rørleggere sjelden tid til å fylle inn nøyaktige timelister. Timer rundes eller glemmes, og lønn er i beste fall et estimat.",
      },
      {
        title: "Ingen bevis på tid på stedet",
        body: "Når en kunde bestrider hvor lang tid en reparasjon tok, finnes det ingen objektiv registrering. En GPS-bekreftet digital innsjekk er lett å fremlegge — en papir-timeliste er det ikke.",
      },
      {
        title: "Kjørelengde mellom jobber usporet",
        body: "Reise mellom utrykninger summerer seg over uken og måneden, men uten automatisk fangst blir den sjelden fakturert eller refundert riktig.",
      },
      {
        title: "Lønnsforberedelse er treg",
        body: "Ved månedsslutt tar det å jakte på timelister, korrigere feil og bygge lønnseksporten tid administrasjonsteamet ditt ikke burde bruke.",
      },
    ],
    features: [
      {
        title: "Stedsverifisert innsjekk med ett trykk på hver utrykning",
        body: "Ankomst og avgang logget med ett trykk, med stedsverifisering av stedet. Raskt nok for akuttsamtaler; presist nok for tvister.",
        plan: "Time Basic",
      },
      {
        title: "Automatisk pausefradrag",
        body: "Obligatoriske pauser trekkes fra total tid automatisk — ingenting å fylle inn, ingen lønnsfeil.",
        plan: "Time Basic",
      },
      {
        title: "Kjørelengde fanget per tur",
        body: "Konfigurer transporttyper og refusjonssatser. Kilometer logges automatisk og knyttes til riktig kunde eller prosjekt.",
        plan: "Add-on",
      },
      {
        title: "Fraværs- og sykmeldingshåndtering",
        body: "Arbeidere sender inn sykmeldinger digitalt fra appen. Opprett fraværstypene teamet ditt faktisk bruker, inkludert værforsinkelser.",
      },
      {
        title: "Lønnseksport med ett klikk",
        body: "Alle time- og kilometerdata eksporteres rent for lønnsleverandøren din — ingen manuell forberedelse, ingen Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Digital tidsregistrering",
      title: "Rask stedsverifisert innsjekk for hver utrykning",
      body: "Done-it dekker ethvert lokalt krav til tidsregistrering for rørleggerteam — stedsverifisert innsjekk med ett trykk per utrykning, automatisk pausefradrag og en ren lønnseksport. Ingen papir-timelister, ingen manuell rekonstruksjon.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Rørleggeren kommer til utrykningen", body: "Ett trykk sjekker inn. GPS logger stedet og tiden. Raskt nok for en akuttjobb." },
      { step: "Avgang trykket på vei ut", body: "Utsjekk lukker tidsregistreringen. Kjørelengde til neste jobb begynner å logges automatisk." },
      { step: "Månedsslutt er ett klikk", body: "Alle utrykninger, timer og kilometer eksporteres rent. Lønnsforberedelse går fra en dag til minutter." },
    ],
    stats: [
      { value: "1 trykk", label: "for å sjekke inn — raskt nok for akuttsamtaler" },
      { value: "1 klikk", label: "lønnseksport til leverandøren din" },
      { value: "0", label: "timer rekonstruert fra hukommelsen ved månedsslutt" },
    ],
    quote: {
      text: "Vi pleide å bruke en formiddag ved månedsslutt på å rekonstruere timer. Nå eksporterer og sender vi bare. Lønnsleverandøren får det rent.",
      who: "Eier, rørleggerfirma · 4 rørleggere",
      result: "Lønnsforberedelse ned fra timer til minutter",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Hagearbeid",
    headline: "Tidsregistrering for hagearbeidsfirmaer — sesongarbeidslag, flere steder og kjørelengde i én oversikt",
    intro: "Høysesong betyr mange steder, skiftende arbeidslag og mange kilometer. Done-it holder hver time, hvert fravær og hver tur organisert — så du kan fokusere på arbeidet, ikke papirarbeidet.",
    pains: [
      {
        title: "Sesongarbeidslag er komplekse å håndtere",
        body: "Midlertidige arbeidere, skiftende timeplaner og varierende teamstørrelser gjør nøyaktig timeregistrering nesten umulig med papir. Når du avstemmer ved månedsslutt, mangler det alltid noe.",
      },
      {
        title: "Timer spredt over mange steder",
        body: "Et arbeidslag som besøker tre eller fire steder per dag genererer timer som må fordeles riktig. Manuell registrering fører til feilattribusjon og omstridt lønn.",
      },
      {
        title: "Værstans er vanskelig å dokumentere",
        body: "Stans på grunn av dårlig vær er en reell hendelse i hagearbeid. Uten en digital registrering er det tregt og feilutsatt å dokumentere stans og justere lønn.",
      },
      {
        title: "Kjørelengde mellom steder blir usporet",
        body: "Firmabiler, private kjøretøy og samkjøring krysser mellom steder hele dagen. Uten automatisk fangst kommer de kilometrene aldri inn i kostnadsbildet.",
      },
    ],
    features: [
      {
        title: "Oversikt over arbeidslag på flere steder",
        body: "Se alle arbeidslag, alle steder i sanntid. Vit hvem som jobber hvor, akkurat nå — ikke ved månedsslutt.",
      },
      {
        title: "Skiftplanlegging for sesongteam",
        body: "Planlegg hvem som jobber hvor, håndter endringer i siste liten og send timeplanen til teamets telefoner — uten en telefon til hver person.",
        plan: "Time Pro",
      },
      {
        title: "Tilpassede fraværstyper",
        body: "Opprett tilpassede fraværstyper for din bransje: sesongmessig værstans, foreldrepermisjon, sykedager. Arbeidere laster opp dokumentasjon digitalt fra appen.",
      },
      {
        title: "Kjørelengde med samkjøringssporing",
        body: "Konfigurer transporttyper (firmabil, privatbil, samkjøringssjåfør mot passasjer) med egne satser. Kilometer logges automatisk per tur.",
        plan: "Add-on",
      },
      {
        title: "Overtidsregistrering med permisjonskonvertering",
        body: "Overtid i høysesongen akkumuleres raskt. Hver arbeider kan velge å få den utbetalt eller spare timene som avspasering — sporet transparent.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Digital tidsregistrering",
      title: "Stedsverifisert innsjekk og fraværssporing for sesongbaserte hagearbeidsteam",
      body: "Done-it gir hagearbeidsteam digital tidsregistrering på tvers av alle steder og sesongarbeidere — stedsverifisert innsjekk, tilpassede fraværstyper for værstans og en ren lønnseksport som oppfyller ethvert lokalt krav.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Arbeidslaget sjekker inn på det første stedet", body: "GPS logger ankomst. Timer fordeles automatisk til riktig sted og prosjekt — ingen manuell fordeling ved dagens slutt." },
      { step: "Været stopper arbeidet", body: "Formannen registrerer stansen fra appen — en ren digital registrering, ikke noe ekstra papirarbeid." },
      { step: "Månedsslutt: alle steder, alle arbeidslag, én eksport", body: "Timer, fravær og kjørelengde på tvers av hvert sted og hvert arbeidslagsmedlem eksporteres rent for lønn." },
    ],
    stats: [
      { value: "Automatisk", label: "fangst av kjørelengde — ikke mer kjørebok" },
      { value: "Sanntid", label: "oversikt over arbeidslag på tvers av alle steder" },
      { value: "1 klikk", label: "lønnseksport for alle steder og alle arbeidslag" },
    ],
    quote: {
      text: "Høysesong pleide å bety lønnskaos. Nå sjekker formannen vår appen, alle er gjort rede for, og jeg eksporterer timene på den siste dagen i måneden.",
      who: "Eier, hagearbeidsfirma · 12 sesongarbeidere",
      result: "Ikke mer lønnsrekonstruksjon ved månedsslutt",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Vedlikehold",
    headline: "Tidsregistrering for vedlikeholdsteam — gjentakende stedsbesøk, kostnad per kontrakt og oppmøtebevis i én app",
    intro: "Gjentakende vedlikeholdskontrakter står og faller på kostnadsoversikt. Done-it logger hvert besøk, sporer timer mot kontraktbudsjettet og gir deg — og kundene dine — bevis på tilstedeværelse for hvert besøk.",
    pains: [
      {
        title: "Ingen kostnadsoversikt per vedlikeholdskontrakt",
        body: "Når timer for gjentakende besøk ikke spores mot den spesifikke kontrakten, vet du ikke om en kunde er lønnsom før gjennomgangen ved årsslutt — og da er det for sent å reprise.",
      },
      {
        title: "Kunder forventer bevis på hva som ble gjort",
        body: "Eiendomsforvaltere og eiendomseiere ønsker i økende grad oppmøteregistreringer: hvem kom, når de kom, hva de gjorde. En papirlogg fremlagt uker senere oppfyller ikke SLA-krav.",
      },
      {
        title: "Gjentakende besøk vanskelige å planlegge og logge",
        body: "Roterende skift på tvers av mange gjentakende steder — med ansatte som dekker for hverandre — skaper et planleggings- og timesporingspuslespill som manuelle prosesser ikke kan løse pålitelig.",
      },
      {
        title: "Værstans påvirker utendørs vedlikehold også",
        body: "Plenstell, fasadevedlikehold og utendørs anleggsarbeid møter alle værrelatert stans. Å dokumentere dem riktig krever et digitalt spor.",
      },
    ],
    features: [
      {
        title: "Kontraktbasert kostnadssporing",
        body: "Hver vedlikeholdskontrakt er et prosjekt. Timer logges automatisk mot den, og gir deg et sanntids kostnadstall og en nedtelling av gjenstående timer for hver kunde.",
        plan: "Project Basic",
      },
      {
        title: "GPS-bekreftet oppmøtebevis",
        body: "Tidsstemplede, stedsverifiserte innsjekker er en registrering av hvem som var på stedet, når og hvor lenge — klar til å deles med kunder på forespørsel.",
      },
      {
        title: "Oppdateringer med bilder fra stedet",
        body: "Arbeidere rapporterer problemer, fullførte oppgaver eller ekstraarbeid direkte fra appen — med tekst og bilder. Kunder får en sanntidsregistrering; du har dokumentasjonen for fakturering.",
        plan: "Project Basic",
      },
      {
        title: "Gjentakende skiftplanlegging",
        body: "Planlegg gjentakende besøk på tvers av alle kontrakter, tildel arbeidere og håndter erstatninger — uten en telefon til hver person.",
        plan: "Time Pro",
      },
      {
        title: "Fraværstyper for utendørs vedlikehold",
        body: "Konfigurer værstans og andre fraværstyper. Arbeidere registrerer dem fra appen; dokumentasjon lastes opp digitalt.",
      },
    ],
    compliance: {
      label: "Kontraktbasert tidsregistrering",
      title: "Sanntids kostnadsoversikt per vedlikeholdskontrakt",
      body: "Done-it gir vedlikeholdsteam GPS-bekreftet oppmøtebevis per kontrakt, tilpassede fraværstyper og en sanntidsoversikt over kostnad per gjentakende kunde — og oppfyller ethvert lokalt krav til tidsregistrering uten ekstra maskinvare.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Arbeideren kommer til vedlikeholdsstedet", body: "Stedsverifisert innsjekk logger ankomsttiden og knytter timer til riktig kontrakt. Oppmøtebevis opprettes automatisk." },
      { step: "Oppgaver fullført, problemer logget", body: "Fullført arbeid og eventuelle problemer rapporteres fra appen med bilder. Kunder kan se nøyaktig hva som ble gjort." },
      { step: "Kostnad per kontrakt synlig umiddelbart", body: "Timer akkumuleres mot kontraktbudsjettet i sanntid. Du kjenner marginen før kontrakten fornyes." },
    ],
    stats: [
      { value: "Sanntid", label: "kostnad per vedlikeholdskontrakt" },
      { value: "GPS", label: "oppmøtebevis klart for kunder på forespørsel" },
      { value: "0", label: "kontraktfornyelser uten å kjenne marginen din" },
    ],
    quote: {
      text: "Vi kan fortelle en eiendomsforvalter nøyaktig hvem som var på stedet og når, på to minutter. Det nivået av bevis endret hvordan kunder ser på oss.",
      who: "Driftsleder, vedlikeholdsfirma · 8 arbeidere",
      result: "Kundetvister ned til null",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Butikk og detaljhandel",
    headline: "Timeregistrering for butikker — innstempling på skift, vaktdekning og en lønnsklar eksport, i én app",
    intro: "Åpningsskift, kveldsskift og alltid noen som dekker for noen andre. Done-it gir hver butikk en ryddig digital oversikt over hvem som jobbet hvilket skift — stemplet inn på egen telefon, klart til lønnskjøring ved månedsslutt.",
    pains: [
      {
        title: "Skiftbytter når aldri fram til lønn",
        body: "Noen dekker for en syk kollega, noen blir en time ekstra ved stenging. Det huskes på en lapp, eller ikke i det hele tatt — og lønnen er feil før du har begynt.",
      },
      {
        title: "Flere butikker, én lønnskjøring",
        body: "Ansatte flytter seg mellom avdelinger, deltidsansatte jobber på to steder. Å samle timer per butikk og slå dem sammen for hånd koster en dag hver måned.",
      },
      {
        title: "Pauser og overtid er gjetning",
        body: "Lovpålagte pauser trekkes fra etter hukommelsen og ekstratimer gjøres opp uformelt — nettopp de to tingene et tilsyn spør om først.",
      },
    ],
    features: [
      {
        title: "Stemple inn i butikken",
        body: "Ett trykk på egen telefon når skiftet starter. Ingen terminal ved kassa, ikke noe delt kort, ingen perm ved lagerdøra.",
      },
      {
        title: "Timer per butikk, ansatte på tvers av avdelinger",
        body: "Hver butikk er et prosjekt. Den som dekker en annen avdeling, stemples på den avdelingen — du ser timene der de faktisk ble jobbet.",
      },
      {
        title: "Pauser trekkes fra automatisk",
        body: "Sett pauseregelen én gang, så gjelder den for hvert skift. Overtid registreres i det øyeblikket den oppstår, og hver ansatt velger utbetaling eller avspasering.",
      },
    ],
    compliance: {
      label: "Skiftbasert timeregistrering",
      title: "Dokumentasjon som holder for hvert skift",
      body: "Done-it holder en objektiv digital oversikt over hver inn- og utstempling, med pauser og overtid — dokumentasjonen et tilsyn eller en lønnstvist ber om.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ansatte stempler inn på skiftet", body: "Ett trykk på telefonen når skiftet starter. Dekning for en annen avdeling stemples på den avdelingen." },
      { step: "Du ser hvem som er på jobb, per butikk", body: "Sanntidsoversikt på tvers av alle lokasjoner — oppdag et udekket åpningsskift før dørene åpner." },
      { step: "Lønn i én eksport", body: "Timer, pauser og overtid per ansatt og per butikk, eksportert klart til lønnssystemet." },
    ],
    stats: [
      { value: "1 trykk", label: "for å stemple inn — ingen terminal ved kassa" },
      { value: "Sanntid", label: "oversikt over hvem som er på jobb, i alle butikker" },
      { value: "0", label: "skiftbytter tapt mellom butikkgulvet og lønn" },
    ],
    quote: {
      text: "To butikker og mange deltidsansatte. Før ble halvparten av vaktdekningene gjort opp etter hukommelsen. Nå ligger hver time på riktig butikk, og lønnskjøringen tar minutter.",
      who: "Eier, butikk · 11 ansatte",
      result: "Lønnsforberedelse fra en dag til under en time",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Kontor og profesjonelle tjenester",
    headline: "Timeregistrering for kontorer — timer per kunde og prosjekt, uten regnearket",
    intro: "Regnskapsførere, advokater, arkitekter, byråer og konsulenter fakturerer tiden sin — eller trenger i det minste å vite hvor den ble av. Done-it fører timene på riktig kunde og prosjekt, fra pulten eller på farten.",
    pains: [
      {
        title: "Fakturerbare timer rekonstruert på fredag",
        body: "Tid som skrives ned på slutten av uka, er tid som gjettes. Hele timer forsvinner stille mellom jobben og fakturaen.",
      },
      {
        title: "Ingen anelse om hva en kunde faktisk koster",
        body: "Honoraret ble avtalt for måneder siden. Om jobben fortsatt får plass innenfor det, blir først klart når noen summerer — vanligvis for sent.",
      },
      {
        title: "Hybridarbeid gjør oversikten hullete",
        body: "Deler av teamet er på kontoret, deler hjemme, deler hos kunden. Tilstedeværelse slutter å være et faktum og blir en rekonstruksjon.",
      },
    ],
    features: [
      {
        title: "Timer på kunde og prosjekt",
        body: "Hver føring lander på et prosjekt, så du ser timene per kunde uten å eksportere noe eller spørre noen.",
      },
      {
        title: "Fungerer ved pulten og ute",
        body: "Samme app på telefonen eller i nettleseren. Et kundebesøk teller som arbeid på den kunden, inkludert reisetiden hvis du vil ha den med.",
      },
      {
        title: "Overtid og fravær på ett sted",
        body: "Ekstratimer registreres når de oppstår, ferie og fravær søkes om og godkjennes i appen — ingen parallelle regneark over hvem som er borte når.",
      },
    ],
    compliance: {
      label: "Timeregistrering per kunde",
      title: "En objektiv oversikt over arbeidede timer",
      body: "Done-it holder en digital oversikt over arbeidstid per ansatt, med sporbare endringer — grunnlaget for både lønn og en kundefaktura som holder.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Opprett et kundeprosjekt", body: "Sett det opp én gang med kunden og, hvis du fakturerer mot budsjett, timene du har planlagt." },
      { step: "Teamet fører timer på det", body: "Fra pulten eller telefonen. Hver time lander på en kunde, ikke i noens hukommelse." },
      { step: "Se timer per kunde, eksporter til lønn", body: "Løpende totaler per prosjekt, og en ryddig eksport til lønn eller fakturering ved månedsslutt." },
    ],
    stats: [
      { value: "Per kunde", label: "timer, synlige mens jobben fortsatt pågår" },
      { value: "Kontor eller vei", label: "samme oversikt, telefon og nettleser" },
      { value: "1 eksport", label: "til lønn og fakturering" },
    ],
    quote: {
      text: "Vi pleide å bygge opp igjen hele uka hver fredag. Nå ligger timene allerede på riktig kunde, og jeg ser hvilke saker som spiser opp honoraret.",
      who: "Partner, regnskapskontor · 8 ansatte",
      result: "Fakturerbare timer hentet inn hver måned",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salonger og kroppspleie",
    headline: "Timeregistrering for salonger — skift, deltidstimer og lønn uten papirkalenderen",
    intro: "Frisører, barberere, negle- og skjønnhetsstudioer går på skift og deltidskontrakter. Done-it registrerer hvem som jobbet når, trekker fra pauser automatisk og gir lønn en ryddig fil.",
    pains: [
      {
        title: "Deltidskontrakter, endeløse varianter",
        body: "Tre dager én uke, fire den neste, en lørdag byttet mot en tirsdag. Avtalte timer og jobbede timer glir fort fra hverandre.",
      },
      {
        title: "Timeboka er ikke en timeliste",
        body: "Avtalene sier hva som ble booket, ikke hvem som jobbet. Når lønn eller et tilsyn spør, er bookingsystemet feil svar.",
      },
      {
        title: "Ekstratimer gjøres opp uformelt",
        body: "Å bli igjen for en siste kunde blir husket, lovet — og noen ganger glemt. Det er den typen hull som ender i en tvist.",
      },
    ],
    features: [
      {
        title: "Stemple inn i salongen",
        body: "Ett trykk på egen telefon når skiftet starter — ingen delt terminal i resepsjonen, ingen signaturliste ved speilet.",
      },
      {
        title: "Pauser og deltidsregler én gang",
        body: "Sett pauseregelen og standardtimene én gang; de gjelder for hvert skift, med unntak per person der en kontrakt avviker.",
      },
      {
        title: "Overtid, utbetalt eller avspasert",
        body: "Ekstratimer registreres mens de jobbes, og hver ansatt velger utbetaling eller avspasering — gjort opp i appen, ikke etter hukommelsen.",
      },
    ],
    compliance: {
      label: "Digital timeregistrering",
      title: "Dokumentasjon som holder utover timeboka",
      body: "Done-it registrerer faktisk arbeidstid per ansatt, med pauser og overtid — atskilt fra bookingene, og holdbart når det kontrolleres.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ansatte stempler inn på skiftet", body: "Ett trykk ved ankomst. Pauser trekkes fra etter regelen du satte én gang." },
      { step: "Du ser dagen mens den går", body: "Hvem som er inne, hvem som har pause, hvem som ble igjen — i sanntid, uten å spørre." },
      { step: "Lønn får en ryddig fil", body: "Jobbede timer, pauser og overtid per ansatt, eksportert til lønnssystemet." },
    ],
    stats: [
      { value: "1 trykk", label: "for å stemple inn — ingenting delt, ingenting på papir" },
      { value: "Auto", label: "pausetrekk på hvert skift" },
      { value: "0", label: "sene timer gjort opp etter hukommelsen" },
    ],
    quote: {
      text: "Alle jobber deltid, og hver uke er forskjellig. Appen bare registrerer det — jeg har sluttet å bygge opp igjen måneden i et regneark.",
      who: "Eier, frisørsalong · 6 ansatte",
      result: "Slutt på omstridte timer ved månedsslutt",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Helse og velvære",
    headline: "Timeregistrering for treningssentre, studioer og spa — skift, gruppetimer og lønn i én app",
    intro: "Treningssentre, treningsstudioer, fysioterapiklinikker og spa har lange åpningstider med roterende bemanning. Done-it registrerer hvem som dekket hvilket skift, trekker fra pauser automatisk og eksporterer lønn rent.",
    pains: [
      {
        title: "Lange åpningstider, roterende dekning",
        body: "Tidligskift, kveldstimer og helger, dekket av en blanding av heltids-, deltids- og frilansansatte. Vaktplanen er en plan, ikke en registrering.",
      },
      {
        title: "Gruppetimer og gulvtimer flyter sammen",
        body: "En instruktør holder to gruppetimer og tar resepsjonen imellom. Å betale det riktig krever å vite hva som faktisk ble jobbet, ikke hva som stod på planen.",
      },
      {
        title: "Flere lokasjoner, én administrasjon",
        body: "Ansatte flytter seg mellom studioene. Å slå sammen timene deres for hånd hver måned er tregt og feilutsatt.",
      },
    ],
    features: [
      {
        title: "Stemple inn per studio",
        body: "Ett trykk på telefonen på stedet de jobber. Dekning på et annet studio havner på det studioets timer.",
      },
      {
        title: "Sanntidsoversikt over hvem som er på jobb",
        body: "Se gulvet på tvers av alle lokasjoner i sanntid — et udekket tidligskift er synlig før dørene åpner, ikke etterpå.",
      },
      {
        title: "Pauser og overtid håndtert",
        body: "Pauseregler brukes automatisk, ekstratimer registreres når de oppstår, med utbetaling eller avspasering per ansatt.",
      },
    ],
    compliance: {
      label: "Skiftregistrering på flere steder",
      title: "Objektiv dokumentasjon for hvert skift og sted",
      body: "Done-it holder en digital oversikt over arbeidstid per ansatt og per lokasjon — grunnlaget for riktig lønn og for enhver kontroll av arbeidede timer.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ansatte stempler inn på studioet", body: "Ett trykk når skiftet starter, på stedet der de jobber." },
      { step: "Dekningen er synlig i sanntid", body: "Hvem som er på jobb på hvilken lokasjon akkurat nå, så hull fylles før medlemmene merker det." },
      { step: "Én eksport til lønn", body: "Timer, pauser og overtid per ansatt og per studio, klart til lønnssystemet." },
    ],
    stats: [
      { value: "Per studio", label: "timer, også når ansatte dekker andre steder" },
      { value: "Sanntid", label: "oversikt over gulvet på tvers av lokasjoner" },
      { value: "Auto", label: "pauser og overtid, ingen manuell regning" },
    ],
    quote: {
      text: "To studioer, lange åpningstider og mange vaktdekninger. Nå havner timene på riktig sted, og lønn er én fil.",
      who: "Daglig leder, treningsstudio · 14 ansatte",
      result: "Vaktplan og lønn stemmer endelig",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Servering og hotell",
    headline: "Timeregistrering for kafeer, restauranter og hoteller — skift, pauser og overtid, gjort opp rettferdig",
    intro: "Delte vakter, sene stenginger og en travel lørdag som drar ut. Done-it registrerer timene slik de faktisk jobbes, trekker fra pauser etter din regel og gir lønn én ryddig fil.",
    pains: [
      {
        title: "Skiftet slutter aldri når vaktplanen sier",
        body: "Serveringen drar ut, stengingen tar en halvtime til. De minuttene er ekte arbeid, og å gjøre dem opp etter hukommelsen er slik tvister starter.",
      },
      {
        title: "Delte vakter er vanskelige å registrere",
        body: "Lunsjservering, en pause, så middag. På papiret blir det én lang blokk eller to gjetninger — ingen av dem er sannheten.",
      },
      {
        title: "Høy gjennomtrekk, konstant opplæring",
        body: "Nye ansatte hver sesong. Ethvert system som krever opplæring, overlever ikke den første travle uka.",
      },
    ],
    features: [
      {
        title: "Stemple inn og ut per servering",
        body: "Ett trykk ved start og slutt på hver blokk, så delte vakter registreres slik de skjedde — ikke slått sammen til én.",
      },
      {
        title: "Pauser etter din regel",
        body: "Lovpålagte pauser trekkes fra automatisk, med unntak per person der en kontrakt krever det. Ingen manuell regning ved månedsslutt.",
      },
      {
        title: "Overtid gjøres opp i appen",
        body: "Den ekstra halvtimen etter stenging registreres i det øyeblikket den jobbes, og utbetales eller tas ut som avspasering — den ansatte velger.",
      },
    ],
    compliance: {
      label: "Registrering av delte vakter",
      title: "Dokumentasjon som holder i et team med høy gjennomtrekk",
      body: "Done-it holder en objektiv digital oversikt over hvert skift, hver pause og hver ekstratime — nøyaktig det en lønnskontroll eller et arbeidstilsyn ber om å se.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ansatte stempler inn til servering", body: "Ett trykk når blokken starter; det samme ved slutt. Delte vakter forblir delte." },
      { step: "Du ser gulvet i sanntid", body: "Hvem som er på jobb til kveldens servering, hvem som fortsatt har pause, hvem som ble igjen etter stengetid." },
      { step: "Lønn får de virkelige timene", body: "Arbeidstid, pauser og overtid per ansatt, eksportert i én fil." },
    ],
    stats: [
      { value: "Delte vakter", label: "registrert som to blokker, ikke én gjetning" },
      { value: "Auto", label: "pausetrekk etter din regel" },
      { value: "1 min", label: "å få en nyansatt i gang — det er deres egen telefon" },
    ],
    quote: {
      text: "Stengingen drar alltid ut. Nå logges de minuttene i stedet for å bli kranglet om, og de ansatte kan ta dem ut som avspasering.",
      who: "Eier, restaurant · 17 ansatte",
      result: "Færre tvister, raskere lønn",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Helse og omsorg",
    headline: "Timeregistrering for omsorgsteam — skift, hjemmebesøk og kvalifikasjoner, i én oversikt",
    intro: "Klinikker, hjemmetjenester og omsorgsboliger går på skift, besøk og strenge dokumentasjonskrav. Done-it registrerer arbeidstid per skift og per besøk, og holder ansattdokumentene der du finner dem.",
    pains: [
      {
        title: "Besøk og skift i hvert sitt system",
        body: "Hjemmebesøk ligger i ett verktøy, vaktplantimer i et annet, og lønn får en tredje versjon. Å avstemme dem er en månedlig jobb.",
      },
      {
        title: "Natt- og helgetimer prises feil",
        body: "Ubekvem arbeidstid har egne satser. Når registreringen er omtrentlig, blir lønnen det også — og korrigeringen kommer i etterkant.",
      },
      {
        title: "Sertifikater går ut på dato i stillhet",
        body: "Kvalifikasjoner og sertifikater betyr noe i omsorg, og de ligger i en perm ingen åpner før et tilsyn spør.",
      },
    ],
    features: [
      {
        title: "Stemple inn per skift eller per besøk",
        body: "Ett trykk når skiftet starter, eller på hver klientadresse i hjemmetjenesten — stedfestet der du trenger dokumentasjonen.",
      },
      {
        title: "Timer delt slik du betaler dem",
        body: "Natt-, helge- og ordinære timer registreres hver for seg, så lønnseksporten allerede gjenspeiler hvordan arbeidet faktisk betales.",
      },
      {
        title: "Ansattdokumenter på ett sted",
        body: "Kontrakter, sertifikater og legitimasjon lagret ved siden av personen de hører til — funnet på sekunder når noen spør.",
      },
    ],
    compliance: {
      label: "Skift- og besøksregistrering",
      title: "Dokumentasjon som tåler et tilsyn",
      body: "Done-it holder en digital, objektiv oversikt over arbeidstid per ansatt — timer, pauser og fordeling av ubekvem arbeidstid — sammen med dokumentene som hører til hver person.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Stemple inn på skiftet eller besøket", body: "Ett trykk på stedet eller på klientens adresse, stedfestet der dokumentasjon trengs." },
      { step: "Timene havner i riktig kategori", body: "Ordinære timer, nattetimer og helgetimer registreres hver for seg mens de oppstår." },
      { step: "Eksporter til lønn", body: "Én ryddig fil per periode, allerede delt slik du betaler den." },
    ],
    stats: [
      { value: "Per besøk", label: "eller per skift — det teamet ditt går på" },
      { value: "Delt", label: "natt- og helgetimer, registrert slik de jobbes" },
      { value: "Arkivert", label: "sertifikater lagret per ansatt" },
    ],
    quote: {
      text: "Hjemmebesøk og vaktplanskift ligger endelig i samme oversikt. Lønn har sluttet å være en avstemmingsøvelse.",
      who: "Koordinator, hjemmetjeneste · 22 ansatte",
      result: "Én oversikt i stedet for tre",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
